import { useParams } from "react-router-dom";
import PitchImageGallery from "../components/PitchImageGallery";
import { useContext, useEffect, useState, useMemo } from "react";
import { PitchContext } from "../context/PitchContex";
import { FaLocationDot } from "react-icons/fa6";
import AddressAndContact from "../components/AddressAndContact";
import AmenitiesAndBook from "../components/AmenitiesAndBook";
import DetailedDescription from "../components/DetailedDescription";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import axios from "axios";
import { toast } from "react-toastify";
import Spinner from "../components/Spinner.jsx";
import CommentSection from "../components/CommentSection.jsx";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import PitchItemHorizontal from "../components/PitchItemHorizontal"; // Import the component
import BookingForm from "../components/BookingForm.jsx";

function Pitch() {
  const { slug } = useParams();
  const { backendURL, token, user } = useContext(PitchContext);
  const [pitch, setPitch] = useState();
  const [rating, setRating] = useState();
  const [reviewSubmit, setReviewSubmit] = useState();
  const [mapCenter, setMapCenter] = useState(null);

  const [showOwnerPitches, setShowOwnerPitches] = useState(false);
  const weeklyBlockedTimes = {
    1: ["18:00", "19:00"], // Every Monday
    5: ["15:00", "16:00"], // Every Friday
  };
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyCq31_wo46xhO_69AYISoLFGZBPcVuuxa8",
  });

  const mapContainerStyle = {
    width: "100%",
    height: "400px",
    borderRadius: "0.5rem",
  };

  const getRating = () => {
    if (pitch) {
      let total = 0;
      let counter = 0;
      pitch.reviews.forEach((review) => {
        total += review.rating;
        counter += 1;
      });
      const tempRating = counter ? total / counter : 0.0;
      setRating(tempRating);
    }
  };

  const getPitch = () => {
    axios
      .post(
        backendURL + `pitch/get/${slug}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        if (response.data.success) {
          setPitch(response.data.pitch);

          const { lat, lng } = response.data.pitch.location.coordinates;
          setMapCenter({ lat, lng });
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error(err.response?.data?.message || "Error fetching pitch");
      });
  };

  useEffect(() => {
    getPitch();
    window.scrollTo(0, 0);
  }, [reviewSubmit, slug]);

  useEffect(() => {
    getRating();
  }, [pitch]);

  const mapComponent = useMemo(
    () =>
      isLoaded && mapCenter ? (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={14}
        >
          <Marker position={mapCenter} />
        </GoogleMap>
      ) : null,
    [isLoaded, mapCenter]
  );

  return (
    <div className="px-0">
      {pitch ? (
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 py-10">
          {/* Header Section */}
          <div className="mb-8 mx-1">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex flex-col gap-2">
                <h1 className="text-[10px] sm:text-4xl font-bold text-gray-800 tracking-tight">
                  {pitch.name}
                </h1>
                <div className="flex items-center gap-2 text-gray-600">
                  <FaLocationDot className="text-[#4d9041] text-lg" />
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${pitch.location.city}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#4d9041] transition-colors duration-200"
                  >
                    {pitch.location.city}
                  </a>
                </div>
                <div className="flex gap-2">
                  <span className="text-xl font-semibold text-gray-700">
                    Ücret:{" "}
                    <span className="text-[#4d9041]">₺{pitch.price}</span>
                    /saat
                  </span>
                </div>
                {pitch.deposit && (
                  <span className="text-xl font-semibold text-gray-700">
                    Kapora:{" "}
                    <span className="text-[#4d9041]">₺{pitch.deposit}</span>
                    /saat
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                {pitch.status !== "active" && (
                  <p className="text-lg text-[#a83232] font-medium bg-red-100 px-3 py-1 rounded-md">
                    Not Available Now
                  </p>
                )}
                <div className="flex flex-col sm:items-end gap-4 text-yellow-500">
                  <div className="flex">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-5 h-5"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.518 4.67a1 1 0 00.95.69h4.91c.969 0 1.372 1.24.588 1.81l-3.974 2.89a1 1 0 00-.364 1.118l1.518 4.67c.3.921-.755 1.688-1.54 1.118l-3.974-2.89a1 1 0 00-1.176 0l-3.974 2.89c-.785.57-1.84-.197-1.54-1.118l1.518-4.67a1 1 0 00-.364-1.118l-3.974-2.89c-.784-.57-.38-1.81.588-1.81h4.91a1 1 0 00.95-.69l1.518-4.67z" />
                    </svg>
                    <span className="text-gray-700 font-medium">
                      {rating && rating.toFixed(2)} ({pitch.reviews.length})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Image Gallery */}
          <div className="mb-10">
            <PitchImageGallery images={pitch.images} />
          </div>

          {/* Divider */}
          <hr className="w-full border-none h-px bg-gray-300 mb-10" />

          {/* Booking Form */}
          <div className="mb-10">
            <BookingForm
              pitch={pitch}
              weeklyBlockedTimes={weeklyBlockedTimes}
            />
          </div>

          {/* Divider */}
          <hr className="w-full border-none h-px bg-gray-300 mb-10" />

          {/* Amenities and Details Grid */}
          <div className="grid gap-8 lg:grid-cols-2 max-w-7xl mx-auto mt-12 mb-10">
            {/* Left Column: Amenities and Owner's Other Pitches */}
            <div className="flex flex-col gap-8">
              <div className="bg-white rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-lg">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Harita üzerinde konumu
                </h2>
                {mapComponent}
              </div>

              {/* Owner's Other Pitches */}
              <div className="bg-white rounded-xl shadow-md p-6 transition-all duration-300 ">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => setShowOwnerPitches(!showOwnerPitches)}
                >
                  <h2 className="text-xl font-semibold text-gray-800">
                    Saha sahibinin bütün sahaları
                  </h2>
                  {showOwnerPitches ? (
                    <FaArrowUp className="text-[#4d9041] text-lg" />
                  ) : (
                    <FaArrowDown className="text-[#4d9041] text-lg" />
                  )}
                </div>
                {showOwnerPitches && (
                  <div className="mt-4 space-y-4 animate-fade-in">
                    {pitch.ownerID.pitches.length > 0 ? (
                      pitch.ownerID.pitches.map((otherPitch) => (
                        <PitchItemHorizontal
                          key={otherPitch._id}
                          id={otherPitch._id}
                          slug={otherPitch.slug}
                          name={otherPitch.name}
                          location={otherPitch.location.city} // Adjust based on your schema
                          price={otherPitch.price}
                          status={otherPitch.status}
                          showBookingsButton={false} // Hide the bookings button
                        />
                      ))
                    ) : (
                      <p className="text-gray-600">
                        No other pitches by this owner.
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="bg-white rounded-xl  p-6 transition-all duration-300">
                <DetailedDescription description={pitch.description} />
              </div>
            </div>

            {/* Right Column: Address, Map, Description */}
            <div className="flex flex-col gap-8">
              <div className="bg-white rounded-xl  p-6 transition-all duration-300 ">
                <AddressAndContact
                  phone={pitch.ownerID.phone}
                  email={pitch.ownerID.email}
                  location={pitch.location.coordinates}
                  address={pitch.location.address}
                />
              </div>
              <div className="bg-white rounded-xl  p-6 transition-all duration-300 ">
                <AmenitiesAndBook
                  availability={pitch.availability}
                  amenities={pitch.amenities}
                  price={pitch.price}
                  cancelable={true}
                  bookingQuantity={pitch.bookingQuantity}
                />
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div className="bg-white rounded-lg sm:p-6">
            <CommentSection
              reviews={pitch.reviews}
              pitchID={pitch._id}
              getPitch={getPitch}
              setReviewSubmit={setReviewSubmit}
            />
          </div>
        </div>
      ) : (
        <Spinner />
      )}

      {/* Custom Animation */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default Pitch;
